import { useMemo, useState, useCallback, useEffect, useContext } from "react";
import { Option } from "@/modules/common/components/input/dropdowns/auto-complete";
import { SortOrder } from "@/modules/common/components/input/sort-input-toggle";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { useLeadRatings } from "@/modules/lead-page/new/hooks/ratings/use-lead-reviews.hook";

export const RATING_OPTIONS: Option[] = [
  { label: "All ratings", value: "" },
  { label: "5 stars", value: "5" },
  { label: "4 stars", value: "4" },
  { label: "3 stars", value: "3" },
  { label: "2 stars", value: "2" },
  { label: "1 star", value: "1" },
];

export const useRatingFilter = (
  onRatingChange?: (rating: number | undefined) => void,
  onSortOrderChange?: (sortOrder: SortOrder) => void
) => {
  const { lead } = useContext(LeadPageContext);
  const [selectedRating, setSelectedRating] = useState<Option | null>(
    RATING_OPTIONS[0] // Default to "All ratings"
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

  const handleRatingChange = useCallback((selected: Option | null) => {
    setSelectedRating(selected);
  }, []);

  const handleSortOrderChange = useCallback(
    (newSortOrder: SortOrder) => {
      setSortOrder(newSortOrder);
      if (onSortOrderChange) {
        onSortOrderChange(newSortOrder);
      }
    },
    [onSortOrderChange]
  );

  // Map the selected option value to actual rating number for API
  const ratingValue = useMemo(() => {
    if (!selectedRating || selectedRating.value === "") {
      return undefined;
    }
    return parseInt(selectedRating.value, 10);
  }, [selectedRating]);

  // Map sort order to API parameters
  const sortParams = useMemo(() => {
    const direction = sortOrder === "newest" ? "DESC" : "ASC";
    return {
      sort: `createdAt:${direction}`,
    };
  }, [sortOrder]);

  // Fetch ratings with current filter to get count
  const { count } = useLeadRatings({
    filters: {
      ...(lead?.data?.lead?.journey_uid && { journeyUIds: lead.data.lead.journey_uid }),
      ...(ratingValue !== undefined && { ratings: [ratingValue] }),
      page: 1,
      pageSize: 1, // Only need count, so minimal pageSize
      ...sortParams,
    },
    enabled: !!lead?.data?.lead?.journey_uid,
  });

  // Notify parent component when rating changes
  useEffect(() => {
    if (onRatingChange) {
      onRatingChange(ratingValue);
    }
  }, [ratingValue, onRatingChange]);

  // Handle both single and array selections (for type compatibility)
  const handleRatingFilterChange = useCallback(
    (selected: any) => {
      const singleOption = Array.isArray(selected) ? selected[0] : selected;
      handleRatingChange(singleOption);
    },
    [handleRatingChange]
  );

  // Memoize the filter hook result to prevent unnecessary re-renders
  const ratingFilterHookResult = useMemo(
    () => ({
      options: RATING_OPTIONS,
      selectedOption: selectedRating,
      handleChange: handleRatingFilterChange,
      placeholder: "Filter by rating",
      fullWidth: false,
      loading: false,
      customPlaceholderSx: { width: 160 },
      ratingValue, // Keep this for API usage
      sortOrder, // Add sort order
      handleSortChange: handleSortOrderChange, // Add sort change handler
    }),
    [
      selectedRating,
      handleRatingFilterChange,
      ratingValue,
      sortOrder,
      handleSortOrderChange,
    ]
  );

  return {
    ratingFilterHookResult,
    ratingValue, // Export for use in ReviewsContent
    sortOrder, // Export for use in ReviewsContent
    count, // Export count for use when filtering
  };
};
